import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { isNcl } from "utils/app/utils";
import _ from "lodash";
import {
    SaaSDisabled,
    NoLocalMessage,
    NeedSaaSRedirect,
    NoContainerMessage,
    UpgradeRequired,
    ComingSoonMessage,
    NoPermissionMessage,
    ServiceNeedsSaaSIsDisabled,
    ServiceNeedsThirdPartyIsDisabled,
    PullingContainerMessage,
    DeprecatedService,
    NotSupportedInSimulatorMode,
    RouteNotFound
} from "./NoAccessComponents"
import usePermission from "utils/usePermission";
import useCurrentService from "utils/useCurrentService";
import { isVirtualTourEnabled } from "../../../offline/utils";

const ShowAngular = React.memo(() => {
    const dispatch = useDispatch();

    useEffect(() => {
        const showWrapper = () => {
            dispatch({
                type: "APP:SHOW-NG-WRAPPER"
            });
        };

        setTimeout(() => {
            showWrapper();
        }, 100);

        return () => {
            dispatch({
                type: "APP:HIDE-NG-WRAPPER"
            });
        }
    }, [dispatch]);

    return "";
});

export default ({ children, subService, featureTargets }) => {
    const supportedServices = useSelector(state => state.app.supportServices);
    const activeService = useCurrentService();
    const { selectedAgent, selectedAccount, isDemo } = useSelector(state => state.tenancy);
    const currentService = subService && { service: subService }
    const location = useLocation();

    const needsConnector = _.get(currentService, "service.isServiceIntegration") && _.get(currentService, "service.needsConnector") && !selectedAgent;

    const supportServiceKey = _.get(currentService, "service.supportServiceKey");
    const isDeprecatedService= _.get(activeService, "isDeprecated");

    const darkSite = process.env.REACT_APP_IS_DARK === 'true';
    const infraKey = _.get(currentService, "service.agent");
    const allowedCloudProviders = _.get(currentService, "service.cloudProviders");

    let containerNotAvailable = infraKey && _.get(selectedAgent, `agent.clientInfraStatus.${infraKey}.status`, "inactive") !== "active";
    let pullingContainer = containerNotAvailable && _.isEmpty(_.get(selectedAgent,`agent.clientInfraStatus.${infraKey}`));

    if(featureTargets?.activeContainers) {
        const allActive = _.every(featureTargets.activeContainers, container => _.get(selectedAgent, `agent.clientInfraStatus.${container}.status`, "inactive") === "active");
        if(!allActive) {
            containerNotAvailable = true;
            pullingContainer = _.some(featureTargets.activeContainers, container => _.isEmpty(_.get(selectedAgent, `agent.clientInfraStatus.${container}`)))
        }
    }

    const useDockerInfra = _.get(selectedAgent, 'agent.useDockerInfra', false) || darkSite;
    const needHttpRedirect = process.env.REACT_APP_IS_SAAS !== "true" && window.location.protocol === "http:" && _.get(currentService, "service.noLocalHttp", false) && !_.get(supportedServices, "useCompliancePrivateIpContainerMode", false);
    const noLocal = process.env.REACT_APP_IS_SAAS !== "true" && _.get(currentService, "service.noLocal", false);
    const isServiceThirdParty = _.get(currentService, "service.isThirdParty", false);
    const isThirdPartyServicesEnabled = _.get(selectedAccount, "is3rdPartyServicesEnabled", false)

    const comingSoon = (allowedCloudProviders && selectedAgent) ? !allowedCloudProviders.includes(selectedAgent.provider) : false;

    const complianceTab = _.get(currentService, "service.to") === "/governance";
    const isDigitalAdvisor = currentService?.newKey === "NetApp.DigitalAdvisor";
    const isCbsInLegacyDemoMode = activeService?.newKey === "NetApp.BackupCbs" && isDemo;
    const hasPermission = usePermission(currentService?.permissionKeys, !complianceTab && !isDigitalAdvisor);

    const saasIsDisabled = process.env.REACT_APP_IS_SAAS === "true" && !selectedAccount.isSaas;
    const noThirdPartyService = isServiceThirdParty && !isThirdPartyServicesEnabled;

    const isComplianceUsingPrivateIpMode = (currentService?.key === "cloud-compliance") && _.get(supportedServices, "useCompliancePrivateIpContainerMode", false);

    const serviceFunctionsInLocal = supportServiceKey && _.get(supportedServices, `servicesInformation.${supportServiceKey}.govSupported`, false);
    const serviceNeedsSaaSisDisabled = process.env.REACT_APP_IS_SAAS === "false" && infraKey && !selectedAccount.isSaas && !serviceFunctionsInLocal;
    const isCvoInSimulator = isVirtualTourEnabled() && location.pathname.startsWith('/storage/cloud-volumes-ontap');

    if (saasIsDisabled) {
        return <SaaSDisabled/>
    } else if (!isComplianceUsingPrivateIpMode && serviceNeedsSaaSisDisabled) {
        return <ServiceNeedsSaaSIsDisabled/>
    } else if (isDeprecatedService) {
        return <DeprecatedService currentService={activeService}/>
    } else if (!hasPermission({})) {
        return <NoPermissionMessage/>
    } else if (noLocal) {
        return <NoLocalMessage/>
    } else if (needHttpRedirect && useDockerInfra) {
        return <NeedSaaSRedirect/>
    } else if (comingSoon) {
        return <ComingSoonMessage/>
    } else if (!needsConnector && infraKey && !useDockerInfra && !complianceTab && selectedAgent) {
        return <UpgradeRequired/>
    } else if (!needsConnector && containerNotAvailable && selectedAgent && pullingContainer && !isCbsInLegacyDemoMode) {
        return <PullingContainerMessage/>
    } else if (!needsConnector && containerNotAvailable && !complianceTab && selectedAgent && !isCbsInLegacyDemoMode) {
        return <NoContainerMessage/>
    } else if (noThirdPartyService) {
        return <ServiceNeedsThirdPartyIsDisabled/>
    } else if (isCvoInSimulator) {
        return <NotSupportedInSimulatorMode />
    } else if (isNcl()) {
        return children || <RouteNotFound />
    } else {
        //TODO: Handle this later for SaaS after talking to cvo team
        return children || <ShowAngular/>
    }
};
